import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  ChevronDown,
  Menu,
  Phone,
  MapPin,
  Clock,
  Sparkles,
  X,
} from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "./ui/sheet";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { venues } from "../mock/venues";
import { siteInfo } from "../mock/guide";
import BookingProvider, { useBooking } from "./BookingDialog";
import SideNav from "./SideNav";
import StickyVenueBadge from "./StickyVenueBadge";

const HeaderInner = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { open } = useBooking();
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-300 ${
        scrolled
          ? "bg-background/92 backdrop-blur-xl border-b border-border/60"
          : "bg-gradient-to-b from-background/90 to-transparent"
      }`}
    >
      <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between gap-4 px-4 sm:px-6 lg:px-8">
        <Link to="/" className="group flex items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-full border border-primary/40 bg-primary/10">
            <Sparkles className="h-4 w-4 text-primary" />
          </span>
          <span className="leading-tight">
            <span className="block font-serif text-base sm:text-lg text-foreground group-hover:text-primary transition-colors">
              Melbourne Brothels Guide
            </span>
            <span className="hidden text-[10px] uppercase tracking-[0.28em] text-muted-foreground sm:block">
              Est. VIC · Legal & Regulated
            </span>
          </span>
        </Link>

        <nav className="hidden items-center gap-7 lg:flex">
          <Link
            to="/"
            className={`text-sm font-medium transition-colors hover:text-primary ${
              pathname === "/" ? "text-primary" : "text-foreground/80"
            }`}
          >
            The Guide
          </Link>

          <DropdownMenu>
            <DropdownMenuTrigger className="flex items-center gap-1 text-sm font-medium text-foreground/80 outline-none transition-colors hover:text-primary data-[state=open]:text-primary">
              Venues <ChevronDown className="h-4 w-4" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-72 bg-popover border-border/60">
              {venues.map((v) => (
                <DropdownMenuItem key={v.slug} asChild className="cursor-pointer">
                  <Link to={`/#${v.slug}`} className="flex flex-col items-start gap-0.5 py-2">
                    <span className="font-serif text-[15px] text-foreground">{v.name}</span>
                    <span className="text-xs text-muted-foreground">
                      {v.suburb} · {v.badge}
                    </span>
                  </Link>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <Link
            to="/employment"
            className={`text-sm font-medium transition-colors hover:text-primary ${
              pathname === "/employment" ? "text-primary" : "text-foreground/80"
            }`}
          >
            Work
          </Link>
          <a
            href="#contact"
            className="text-sm font-medium text-foreground/80 transition-colors hover:text-primary"
          >
            Contact
          </a>
        </nav>

        <div className="flex items-center gap-3">
          <Link
            to="/employment"
            data-testid="header-job-enquiries-btn"
            className="btn-pink-glow hidden px-5 py-2.5 text-sm sm:inline-flex"
          >
            Adult Job Enquiries
          </Link>

          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger className="lg:hidden rounded-md border border-border/60 p-2 text-foreground">
              <Menu className="h-5 w-5" />
            </SheetTrigger>
            <SheetContent side="right" className="w-[330px] overflow-y-auto bg-background border-border">
              <div className="mt-8 flex flex-col gap-5">
                <Link to="/" onClick={() => setMobileOpen(false)} className="text-lg text-foreground">
                  The Guide
                </Link>
                <div className="h-px bg-border/60" />
                <span className="text-[10px] uppercase tracking-[0.3em] text-primary">Venues</span>
                {venues.map((v) => (
                  <Link
                    key={v.slug}
                    to={`/#${v.slug}`}
                    onClick={() => setMobileOpen(false)}
                    className="font-serif text-base text-foreground/85 transition-colors hover:text-primary"
                  >
                    {v.name}
                  </Link>
                ))}
                <div className="h-px bg-border/60" />
                <Link
                  to="/employment"
                  onClick={() => setMobileOpen(false)}
                  className="text-lg text-foreground"
                >
                  Work in the Industry
                </Link>
                <div className="mt-2 border-t border-border pt-4">
                  {pathname === "/" ? <SideNav onNavigate={() => setMobileOpen(false)} /> : null}
                </div>
                <button
                  className="btn-pink-glow mt-2 w-full"
                  onClick={() => {
                    setMobileOpen(false);
                    open({});
                  }}
                  data-testid="mobile-enquiry-btn"
                >
                  Make an Enquiry
                </button>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};

const Footer = () => (
  <footer id="contact" className="relative border-t border-border bg-muted">
    <div className="hairline absolute inset-x-0 top-0" />
    <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
      <div className="grid gap-12 lg:grid-cols-[1.2fr_1fr_1fr]">
        <div>
          <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-full border border-primary/40 bg-primary/10">
              <Sparkles className="h-4 w-4 text-primary" />
            </span>
            <span className="font-serif text-xl text-foreground">{siteInfo.name}</span>
          </div>
          <p className="mt-5 max-w-md text-sm leading-relaxed text-muted-foreground">
            {siteInfo.tagline}. Sex work has been decriminalised in Victoria through the 2022 and
            2023 reforms. Venues listed here operate as ordinary commercial businesses under
            planning, safety and workplace law.
          </p>
          <p className="mt-5 text-xs text-muted-foreground/70">
            Strictly 18+. All workers are independent and self-employed.
          </p>
        </div>

        <div>
          <h4 className="mb-5 text-[11px] uppercase tracking-[0.28em] text-primary">Venues</h4>
          <ul className="space-y-3">
            {venues.map((v) => (
              <li key={v.slug}>
                <Link
                  to={`/#${v.slug}`}
                  className="text-sm text-muted-foreground transition-colors hover:text-primary"
                >
                  {v.name}
                </Link>
              </li>
            ))}
            <li>
              <Link
                to="/employment"
                className="text-sm text-muted-foreground transition-colors hover:text-primary"
              >
                Employment
              </Link>
            </li>
          </ul>
        </div>

        <div>
          <h4 className="mb-5 text-[11px] uppercase tracking-[0.28em] text-primary">
            Venue Locations
          </h4>
          <ul className="space-y-4">
            {venues.map((v) => (
              <li key={v.slug} className="text-sm">
                <span className="block font-serif text-foreground">{v.name}</span>
                <span className="text-muted-foreground">{v.contact.address}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="mt-14 flex flex-col items-center justify-between gap-4 border-t border-border/60 pt-8 sm:flex-row">
        <p className="text-xs text-muted-foreground">
          © {siteInfo.year} {siteInfo.name}
        </p>
        <div className="flex items-center gap-6 text-xs text-muted-foreground">
          <span className="flex items-center gap-1.5">
            <MapPin className="h-3.5 w-3.5 text-primary" /> Melbourne, VIC
          </span>
          <span className="flex items-center gap-1.5">
            <Clock className="h-3.5 w-3.5 text-primary" /> Confirm hours with each venue
          </span>
        </div>
      </div>
    </div>
  </footer>
);

const Layout = ({ children }) => {
  const { pathname } = useLocation();
  const isGuide = pathname === "/";

  const { hash } = useLocation();

  useEffect(() => {
    if (hash) {
      const id = hash.replace("#", "");
      const tryScroll = (attempt = 0) => {
        const el = document.getElementById(id);
        if (el) {
          window.scrollTo({
            top: el.getBoundingClientRect().top + window.scrollY - 110,
            behavior: "smooth",
          });
        } else if (attempt < 20) {
          setTimeout(() => tryScroll(attempt + 1), 100);
        }
      };
      tryScroll();
      return;
    }
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [pathname, hash]);

  return (
    <BookingProvider venueNames={venues.map((v) => v.name)}>
      <div className="min-h-screen bg-background">
        <HeaderInner />
        <div className="pt-[76px]">
          <div className="mx-auto flex max-w-[1500px] gap-8 px-4 sm:px-6 lg:px-8">
            {isGuide ? (
              <aside className="hidden w-[195px] flex-shrink-0 lg:block">
                <div className="sticky top-[92px] max-h-[calc(100vh-120px)] overflow-y-auto border-r border-border py-8 pr-3">
                  <SideNav />
                </div>
              </aside>
            ) : null}
            <main className="min-w-0 flex-1">{children}</main>
          </div>
        </div>
        <Footer />
        {isGuide ? <StickyVenueBadge /> : null}
      </div>
    </BookingProvider>
  );
};

export { X };
export default Layout;
